/**
 * The three visibility-range warnings of GeometryInstance3D::get_configuration_warnings
 * (scene/3d/visual_instance_3d.cpp). Its two `forward_plus` warnings are not
 * ported: they read the project's renderer setting, which a single `.tscn` lacks.
 */
import { scalarSlotValue } from '../../../godot/variant/variantBool.js';
import { ruleRegistry } from '../../../linter/RuleRegistry.js';
import { descendsFromClass } from '../../../godot/classdb/classBaseTypes.js';
import { isZeroApprox } from '../../../godot/math/math.js';
import { ruleInt } from '../../../godot/variant/intSlots.js';
import { armEmits, groundedArm, reportArm } from '../../../linter/ruleArms.js';
const arms = {
    endBeforeBegin: groundedArm('geometryinstance3d-visibility-range-end-before-begin', {
        kind: 'configuration-warning',
    }),
    beginFadeWithoutMargin: groundedArm('geometryinstance3d-visibility-range-begin-fade-without-margin', {
        kind: 'configuration-warning',
    }),
    endFadeWithoutMargin: groundedArm('geometryinstance3d-visibility-range-end-fade-without-margin', {
        kind: 'configuration-warning',
    }),
};
const FADE_SELF = 1;
const FADE_DEPENDENCIES = 2;
/** `Math::is_zero_approx`, against the engine's own tolerance. */
function isZeroish(raw) {
    if (raw === undefined)
        return true;
    const n = scalarSlotValue(raw);
    // Unreadable text is not a distance the fade can start from, so it reads as
    // the zero default. `nan` is a value the slot holds, and `is_zero_approx`
    // says false for it, so it must reach the comparison instead.
    return n === null || isZeroApprox(n);
}
function checkGeometryInstance3D(context) {
    const diagnostics = [];
    const { node } = context;
    // `CSGShape3D::get_configuration_warnings` chains to `Node`'s (csg_shape.cpp:978),
    // so no warning here reaches a CSG shape. Every other override chains through
    // this class (gpu_particles_3d.cpp:337, cpu_particles_3d.cpp:215,
    // soft_body_3d.cpp:402, sprite_3d.cpp:1470).
    if (descendsFromClass(node.type, 'CSGShape3D'))
        return diagnostics;
    const props = node.rawProperties;
    // `ruleInt`, not a string compare: the slot is an INT, so `1.0` and `01` are
    // both the 1 Godot stores.
    const fadeMode = ruleInt(props.visibility_range_fade_mode, 0);
    const fades = fadeMode === FADE_SELF || fadeMode === FADE_DEPENDENCIES;
    // scene/3d/visual_instance_3d.cpp: !is_zero_approx(visibility_range_end) &&
    // visibility_range_end <= visibility_range_begin
    if (!isZeroish(props.visibility_range_end)) {
        const end = scalarSlotValue(props.visibility_range_end);
        const begin = scalarSlotValue(props.visibility_range_begin ?? '0');
        // No finiteness guard: `end <= begin` is false when either side is `nan`,
        // in JS as in C++, and an infinite begin is the engine's own warning
        // (visual_instance_3d.cpp:512).
        if (end !== null && begin !== null && end <= begin) {
            reportArm(diagnostics, arms.endBeforeBegin, node, `${node.type} visibility range's End distance (${end}) is set to a non-zero value, but is lower than or equal to the Begin distance (${begin}). This means the node will never be visible. Set End to 0 or to a value greater than Begin.`);
        }
    }
    // scene/3d/visual_instance_3d.cpp: fade_mode in {SELF, DEPENDENCIES} &&
    // !is_zero_approx(visibility_range_begin) && is_zero_approx(visibility_range_begin_margin)
    if (fades && !isZeroish(props.visibility_range_begin) && isZeroish(props.visibility_range_begin_margin)) {
        reportArm(diagnostics, arms.beginFadeWithoutMargin, node, `${node.type} is configured to fade in smoothly over distance, but 'visibility_range_begin_margin' is 0. Increase Visibility Range Begin Margin above 0 for the fade transition to be noticeable.`);
    }
    // scene/3d/visual_instance_3d.cpp: fade_mode in {SELF, DEPENDENCIES} &&
    // !is_zero_approx(visibility_range_end) && is_zero_approx(visibility_range_end_margin)
    if (fades && !isZeroish(props.visibility_range_end) && isZeroish(props.visibility_range_end_margin)) {
        reportArm(diagnostics, arms.endFadeWithoutMargin, node, `${node.type} is configured to fade out smoothly over distance, but 'visibility_range_end_margin' is 0. Increase Visibility Range End Margin above 0 for the fade transition to be noticeable.`);
    }
    return diagnostics;
}
const geometryInstance3DValidationRule = {
    meta: {
        name: 'valid-geometryinstance3d-visibility-range',
        description: "Validates GeometryInstance3D's visibility-range cross-field consistency (End vs Begin distance, fade transitions needing a non-zero margin)",
        category: 'validation',
        // `descendsFromClass`, not `applicableNodeTypes`: RuleRegistry matches that by exact
        // name, so it would miss MeshInstance3D, Sprite3D, Label3D and GPUParticles3D.
        // The matcher keeps the CSG shapes, as the Viewport size rule keeps SubViewport.
        applicableNodeTypeMatcher: (nodeType) => descendsFromClass(nodeType, 'GeometryInstance3D'),
        emits: armEmits(arms),
    },
    check: checkGeometryInstance3D,
};
ruleRegistry.register(geometryInstance3DValidationRule);
//# sourceMappingURL=linter.js.map